import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';

// Scroll-reveal wrapper
export const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

// Small eyebrow / kicker label
export const Kicker = ({ children, light = false }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-600 tracking-[0.25em] uppercase ${light ? 'text-gold-light' : 'text-gold'}`}>
    <span className={`h-px w-8 ${light ? 'bg-gold-light' : 'bg-gold'}`} />
    {children}
  </span>
);

// Section heading block
export const SectionTitle = ({ kicker, title, sub, light = false, center = false }) => (
  <div className={`${center ? 'text-center mx-auto max-w-2xl' : 'max-w-2xl'}`}>
    {kicker && <div className={center ? 'flex justify-center' : ''}><Kicker light={light}>{kicker}</Kicker></div>}
    <h2 className={`font-display text-3xl md:text-[2.6rem] leading-[1.1] mt-4 ${light ? 'text-white' : 'text-forest'}`}>{title}</h2>
    {sub && <p className={`mt-4 text-[15px] md:text-base leading-relaxed ${light ? 'text-cream/80' : 'text-ink/70'}`}>{sub}</p>}
  </div>
);

// Interior page hero banner
export const PageHero = ({ image, kicker, title, sub }) => (
  <section className="relative h-[56vh] min-h-[420px] flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={image} alt={title} className="w-full h-full object-cover kenburns" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest-dark/85 via-forest-dark/55 to-forest-dark/25" />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
      <Reveal>
        {kicker && <Kicker light>{kicker}</Kicker>}
        <h1 className="font-display text-4xl md:text-6xl text-white leading-[1.05] mt-4 max-w-3xl">{title}</h1>
        {sub && <p className="mt-5 text-cream/85 max-w-xl text-base md:text-lg leading-relaxed">{sub}</p>}
      </Reveal>
    </div>
  </section>
);

// Animated number counter
export const Counter = ({ value, suffix = '', prefix = '', duration = 1800 }) => {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.floor(eased * value));
          if (p < 1) requestAnimationFrame(tick);
          else setN(value);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);
  return <span ref={ref}>{prefix}{n.toLocaleString()}{suffix}</span>;
};

// Primary / secondary buttons as links
export const Btn = ({ to, children, variant = 'primary', className = '' }) => {
  const base = 'inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-600 tracking-wide transition-all duration-300';
  const styles = {
    primary: 'bg-gold text-white hover:bg-gold-light shadow-md hover:shadow-lg',
    dark: 'bg-forest text-cream hover:bg-forest-light shadow-md',
    outline: 'border border-white/60 text-white hover:bg-white hover:text-forest',
    outlineDark: 'border border-forest text-forest hover:bg-forest hover:text-cream',
  };
  return <Link to={to} className={`${base} ${styles[variant]} ${className}`}>{children}</Link>;
};

// Dynamic lucide icon by name
export const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Leaf;
  return <Cmp className={className} strokeWidth={1.6} />;
};
